import React from 'react';
import { createUseStyles } from 'react-jss';
import { Link } from 'react-router-dom';

interface ButtonProps {
  theme?: 'default' | 'main' | 'primary' | 'secondary' | 'simple';
  className?: string;
  onClick?: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'before' | 'after';
  radius?: boolean;
  circle?: boolean;
  to?: string;
  href?: string;
  target?: string;
  style?: React.CSSProperties;
}

const useStyles = createUseStyles({
  btn: {
    backgroundColor: '#ffffff',
    color: '#000000',
    border: '1px solid #f0f0f0',
    borderRadius: '5px',
    padding: '8px 16px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '16px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    whiteSpace: 'nowrap',
    '& svg': {
      width: '0.8em',
      height: '0.8em',
      fill: '#000000',
    },
    '&:hover': {
      borderColor: '#cccccc',
      '& svg': {
        fill: '#000000',
      },
    },
    '&:active': {
      boxShadow: 'inset 0 0 0 1000px rgba(0,0,0,0.1)',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: '0.5 !important',
    },
    '&.btn-primary': {
      backgroundColor: 'var(--dai-primary-color)',
      color: '#ffffff',
      borderColor: 'var(--dai-primary-color)',
      '& svg': {
        fill: '#ffffff',
      },
      '&:hover': {
        backgroundColor: '#ffffff',
        color: 'var(--dai-primary-color)',
        '& svg': {
          fill: 'var(--dai-primary-color)',
        },
      },
    },
    '&.btn-secondary': {
      backgroundColor: 'var(--dai-secondary-color)',
      color: '#ffffff',
      borderColor: 'var(--dai-secondary-color)',
      '& svg': {
        fill: '#ffffff',
      },
      '&:hover': {
        backgroundColor: '#ffffff',
        color: 'var(--dai-secondary-color)',
        '& svg': {
          fill: 'var(--dai-secondary-color)',
        },
      },
    },
    '&.btn-simple': {
      backgroundColor: 'transparent',
      color: '#000000',
      padding: '0',
      border: '0',
      '& svg': {
        fill: '#000000',
      },
    },
  },
  radius: {
    borderRadius: '100px',
  },
  circle: {
    borderRadius: '50%',
  },
});

const Button: React.FC<ButtonProps> = ({
  theme = 'default',
  className = '',
  onClick,
  children,
  disabled,
  icon,
  iconPosition = 'before',
  radius,
  circle,
  style,
  to,
  href,
  target,
  ...rest
}) => {
  const classes = useStyles();

  const buttonClasses = [
    classes.btn,
    radius && classes.radius,
    circle && classes.circle,
    `btn-${theme}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (to) {
    return (
      <Link
        className={buttonClasses}
        to={to}
        target={target}
        rel="noopener noreferrer"
        onClick={onClick}
        style={style}
        {...rest}
      >
        {icon && iconPosition === 'before' && icon}
        {children}
        {icon && iconPosition === 'after' && icon}
      </Link>
    );
  } else if (href) {
    return (
      <a
        className={buttonClasses}
        href={href}
        target={target}
        rel="noopener noreferrer"
        onClick={onClick}
        style={style}
        {...rest}
      >
        {icon && iconPosition === 'before' && icon}
        {children}
        {icon && iconPosition === 'after' && icon}
      </a>
    );
  } else {
    return (
      <button
        className={buttonClasses}
        onClick={onClick}
        disabled={disabled}
        style={style}
        {...rest}
      >
        {icon && iconPosition === 'before' && icon}
        {children}
        {icon && iconPosition === 'after' && icon}
      </button>
    );
  }
};

export default Button;
